import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { documentosService } from "../end-points/documentos";
import { toast } from "@/hooks/use-toast";
import { IDocumento } from "@/types/documento";

const QUERY_KEY = "documentos";
const STATS_QUERY_KEY = "documentos-stats";

/**
 * useDocumentos Hook
 *
 * React Query hook for fetching all documents.
 * Automatically caches and manages document list state.
 */
export const useDocumentos = () => {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => documentosService.findAll(),
  });
};

/**
 * useDocumentosStats Hook
 *
 * React Query hook for fetching document statistics.
 * Returns aggregated stats by document type.
 */
export const useDocumentosStats = () => {
  return useQuery({
    queryKey: [STATS_QUERY_KEY],
    queryFn: () => documentosService.getStats(),
  });
};

/**
 * useDocumento Hook
 *
 * React Query hook for fetching a single document by ID.
 *
 * @param id - Document ID to fetch
 */
export const useDocumento = (id: number) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => documentosService.findOne(id),
    enabled: !!id,
  });
};

/**
 * useCreateDocumento Hook
 *
 * React Query mutation hook for creating a new document.
 *
 * @param showToast - Whether to show toast notifications (default: true)
 */
export const useCreateDocumento = (showToast: boolean = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<IDocumento, "id">) =>
      documentosService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] });
      if (showToast) {
        toast({
          title: "Documento criado",
          description: "Documento criado com sucesso",
          variant: "success",
        });
      }
    },
    onError: (err) => {
      console.error(err);
      if (showToast) {
        toast({
          title: "Erro ao criar documento",
          description: "Não foi possível criar o documento",
          variant: "destructive",
        });
      }
    },
  });
};

/**
 * useUpdateDocumento Hook
 *
 * React Query mutation hook for updating an existing document.
 *
 * @param showToast - Whether to show toast notifications (default: true)
 */
export const useUpdateDocumento = (showToast: boolean = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<IDocumento> }) =>
      documentosService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      if (showToast) {
        toast({
          title: "Documento atualizado",
          description: "Documento atualizado com sucesso",
          variant: "success",
        });
      }
    },
    onError: (err) => {
      console.error(err);
      if (showToast) {
        toast({
          title: "Erro ao atualizar documento",
          description: "Não foi possível atualizar o documento",
          variant: "destructive",
        });
      }
    },
  });
};

/**
 * useDeleteDocumento Hook
 *
 * React Query mutation hook for deleting a document.
 *
 * @param showToast - Whether to show toast notifications (default: true)
 */
export const useDeleteDocumento = (showToast: boolean = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => documentosService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] });
      if (showToast) {
        toast({
          title: "Documento excluído",
          description: "Documento excluído com sucesso",
          variant: "success",
        });
      }
    },
    onError: (err) => {
      console.error(err);
      if (showToast) {
        toast({
          title: "Erro ao excluir documento",
          description: "Não foi possível excluir o documento",
          variant: "destructive",
        });
      }
    },
  });
};

/**
 * useUploadDocumento Hook
 *
 * React Query mutation hook for uploading a new document.
 *
 * @param showToast - Whether to show toast notifications (default: true)
 */
export const useUploadDocumento = (showToast: boolean = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FormData) => documentosService.upload(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] });
      if (showToast) {
        toast({
          title: "Documento enviado",
          description: "Documento enviado com sucesso",
          variant: "success",
        });
      }
    },
    onError: (err) => {
      console.error(err);
      if (showToast) {
        toast({
          title: "Erro ao enviar documento",
          description: "Não foi possível enviar o documento",
          variant: "destructive",
        });
      }
    },
  });
};
